import { useState, useEffect } from 'react'
import { X, Star, Plus, CircleCheck, Circle, ArrowRight, Loader2, Trash2 } from 'lucide-react'
import { fmtMoney, TH_MONTHS } from '../../lib/expense.js'

const FIELD = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-700 outline-none focus:border-brand-400'

// Favorites (recurring) manager. Follows the page's main year/month/branch filter.
// Lists each favorite template with its status for that period (✓ recorded / ○ not
// yet), a per-item + bulk "add this month", full-edit on click, and a remove action.
export default function RecurringDrawer({
  open, onClose, series, branchName, year, month, mainBranch,
  onAddOne, onAddAll, onRemove, onOpenEdit, onOpenRecord, creatingKey,
}) {
  const [confirmRemove, setConfirmRemove] = useState(null) // series pending delete
  const [localBranch, setLocalBranch] = useState('')       // only used when main branch = ทุกสาขา
  useEffect(() => { if (open) setLocalBranch('') }, [open])
  if (!open) return null

  const branches = Object.entries(branchName || {}).sort((a, b) => Number(a[0]) - Number(b[0]))
  const effBranch = mainBranch ? String(mainBranch) : localBranch
  const per = year * 100 + month
  const shown = series
    .filter((s) => !effBranch || String(s.branchId) === String(effBranch))
    .map((s) => ({ ...s, recId: s.byPeriod.get(per) || null }))
    .sort((a, b) => (a.recId ? 1 : 0) - (b.recId ? 1 : 0) || String(a.detail).localeCompare(String(b.detail), 'th'))
  const missing = shown.filter((s) => !s.recId)

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* Header — period is driven by the page's main filter */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[16px] font-semibold text-slate-900"><Star size={18} className="text-amber-400 fill-amber-400" /> รายการโปรด</div>
            <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={18} /></button>
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-[13px] font-medium text-slate-500">{TH_MONTHS[month - 1]} {year + 543}</span>
            {mainBranch ? (
              <span className="ml-auto inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-[12px] text-slate-500">{branchName[String(mainBranch)] || `#${mainBranch}`}</span>
            ) : (
              <select value={localBranch} onChange={(e) => setLocalBranch(e.target.value)} className={`${FIELD} ml-auto`}>
                <option value="">ทุกสาขา</option>
                {branches.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Bulk add */}
        {missing.length > 0 && (
          <div className="px-5 py-2.5 bg-amber-50/70 border-b border-amber-100 flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-amber-800">ค้างบันทึกเดือนนี้ {missing.length} รายการ</span>
            <button onClick={() => onAddAll(missing, year, month)} className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-amber-700 shrink-0"><Plus size={12} /> เพิ่มทั้งหมดที่ค้าง</button>
          </div>
        )}

        {/* Series list */}
        <div className="flex-1 overflow-y-auto p-3">
          {shown.length === 0 ? (
            <div className="text-center text-[13px] text-slate-400 py-12 px-6 leading-relaxed">
              ยังไม่มีรายการโปรด<br />
              <span className="text-[12px]">เปิดค่าใช้จ่าย แล้วกด “⭐ เพิ่มในรายการโปรด” เพื่อบันทึกไว้ใช้ซ้ำ</span>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {shown.map((s) => (
                <div key={s.key} className="group flex items-center gap-3 py-2.5 px-1">
                  {s.recId
                    ? <CircleCheck size={17} className="text-emerald-500 shrink-0" strokeWidth={2} />
                    : <Circle size={17} className="text-amber-400 shrink-0" strokeWidth={2} />}
                  <div className="min-w-0 flex-1">
                    <button onClick={() => onOpenEdit(s)} title="แก้ไขรายการโปรด" className="block w-full text-left text-[13.5px] font-medium text-slate-800 truncate hover:text-brand-600">{s.detail || '—'}</button>
                    <div className="text-[11.5px] text-slate-400 truncate">
                      {branchName[String(s.branchId)] || (s.branchId ? `#${s.branchId}` : '')}{s.amount ? ` · ~${fmtMoney(s.amount)}` : ''}
                    </div>
                  </div>
                  {s.recId ? (
                    <button onClick={() => onOpenRecord(s.recId)} className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 hover:text-emerald-700 shrink-0">บันทึกแล้ว <ArrowRight size={12} /></button>
                  ) : (
                    <button
                      onClick={() => onAddOne(s, year, month)}
                      disabled={creatingKey === s.key}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-60 shrink-0"
                    >{creatingKey === s.key ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} เพิ่มเดือนนี้</button>
                  )}
                  <button onClick={() => setConfirmRemove(s)} title="ลบออกจากรายการโปรด" className="p-1.5 rounded-md text-slate-300 hover:text-rose-600 hover:bg-rose-50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Remove-favorite confirm */}
        {confirmRemove && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6 bg-slate-900/20">
            <div className="w-full max-w-xs rounded-2xl bg-white shadow-xl border border-slate-200 p-5">
              <div className="text-[14.5px] font-semibold text-slate-900 mb-1">ลบออกจากรายการโปรด?</div>
              <p className="text-[12.5px] text-slate-500 mb-4">“{confirmRemove.detail || '—'}” จะไม่แสดงในรายการโปรดอีก — ค่าใช้จ่ายที่บันทึกไปแล้วจะยังอยู่ตามเดิม</p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setConfirmRemove(null)} className="px-3.5 py-2 rounded-lg text-[13px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
                <button onClick={() => { onRemove?.(confirmRemove); setConfirmRemove(null) }} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-rose-700"><Trash2 size={14} /> ลบ</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
